import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, IndianRupee, CheckCircle2, Phone, ChevronRight, Car } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import BookingForm from "@/components/BookingForm";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const routeData: Record<string, {
  city: string; distance: string; time: string; cars: string[];
  highlights: string[]; benefits: string[]; content: string;
}> = {
  "trichy-to-velankanni": {
    city: "Velankanni",
    distance: "155 km",
    time: "3.5 hours",
    cars: ["Sedan - ₹4,840", "Innova - ₹6,390", "Innova Crysta - ₹7,630"],
    highlights: ["Our Lady of Good Health Basilica", "Velankanni Beach", "Pilgrimage destination"],
    benefits: ["Pilgrimage specialized drivers", "Safe family travel", "Punctual service", "Clean vehicles", "Knowledgeable guides", "24/7 support"],
    content: `Traveling from Trichy to Velankanni is one of the most popular pilgrimage routes in South India. Velankanni is famous for the Basilica of Our Lady of Good Health. Our taxi service ensures a peaceful and comfortable journey for your family and pilgrims.`,
  },
  "trichy-to-rameswaram": {
    city: "Rameswaram",
    distance: "225 km",
    time: "4.5 hours",
    cars: ["Sedan - ₹6,800", "Innova - ₹9,050", "Innova Crysta - ₹10,850"],
    highlights: ["Ramanathaswamy Temple", "Pamban Bridge drive", "Dhanushkodi visit"],
    benefits: ["Long distance expertise", "Comfortable for elders", "Experienced highway drivers", "Multi-day tour options", "Transparent pricing", "Safe night driving"],
    content: `Rameswaram is a major spiritual hub and the Pamban Bridge drive is a highlight of this journey. Our drivers are well-versed with the Trichy-Rameswaram highway, ensuring a smooth ride to the holy city.`,
  },
  "trichy-to-tanjore": {
    city: "Tanjore",
    distance: "65 km",
    time: "1.5 hours",
    cars: ["Sedan - ₹2,320", "Innova - ₹2,970", "Innova Crysta - ₹3,490"],
    highlights: ["Visit Brihadeeswarar Temple", "Historical Tanjore Palace", "Smooth highway drive"],
    benefits: ["Door-to-door pickup & drop", "No hidden charges", "Professional drivers", "Religious tour experienced", "Free waiting time", "Clean vehicles"],
    content: `The Trichy to Tanjore route is a classic journey through the heart of Tamil Nadu's heritage. Tanjore, or Thanjavur, is home to the Great Living Chola Temples, which are UNESCO World Heritage Sites.`,
  },
  "trichy-to-pudukkottai": {
    city: "Pudukkottai",
    distance: "60 km",
    time: "1.5 hours",
    cars: ["Sedan - ₹2,180", "Innova - ₹2,780", "Innova Crysta - ₹3,260"],
    highlights: ["Sittannavasal Cave Paintings", "Thirugokarnam Temple", "Royal Heritage City"],
    benefits: ["Short trip discounts", "Instant booking", "Local route experts", "Comfortable sedans", "Clean interiors", "Professional chauffeurs"],
    content: `Pudukkottai is rich in history and culture. Our taxi service from Trichy to Pudukkottai is the fastest and most comfortable way to travel between these two historic cities.`,
  },
  "trichy-to-madurai": {
    city: "Madurai",
    distance: "140 km",
    time: "2.5 hours",
    cars: ["Sedan - ₹4,420", "Innova - ₹5,820", "Innova Crysta - ₹6,940"],
    highlights: ["Meenakshi Amman Temple", "Ancient Temple City", "Well-maintained NH"],
    benefits: ["Tandem city connection", "Cultural tour guides", "Punctual service", "Comfortable for pilgrims", "Transparent pricing", "24/7 support"],
    content: `Traveling from Trichy to Madurai connects two of Tamil Nadu's most ancient and spiritual cities. Madurai, known for its iconic Meenakshi Temple, is easily accessible via the national highway.`,
  },
  "trichy-to-dindigul": {
    city: "Dindigul",
    distance: "105 km",
    time: "2 hours",
    cars: ["Sedan - ₹3,440", "Innova - ₹4,490", "Innova Crysta - ₹5,330"],
    highlights: ["Famous Dindigul Biryani", "Dindigul Fort", "Gateway to Kodaikanal"],
    benefits: ["Efficient travel", "Professional drivers", "Clean cars", "No surge pricing", "Flexible timings", "Local expertise"],
    content: `Dindigul is a major hub and famous for its history and culinary delights. Whether you're traveling for business or transit to Kodaikanal, our Trichy to Dindigul taxi service provides the most comfortable option.`,
  },
  "trichy-to-namakkal": {
    city: "Namakkal",
    distance: "100 km",
    time: "2 hours",
    cars: ["Sedan - ₹3,300", "Innova - ₹4,300", "Innova Crysta - ₹5,100"],
    highlights: ["Namakkal Fort", "Anjaneyar Temple", "Kolli Hills Gateway"],
    benefits: ["Reliable transport", "Safe highway driving", "Punctual pickups", "Professional service", "Clean vehicles", "24/7 availability"],
    content: `Namakkal is known for its majestic fort and the towering Anjaneyar statue. Our service provides a seamless connection from Trichy to Namakkal for both business and leisure travelers.`,
  },
  "trichy-to-karaikudi": {
    city: "Karaikudi",
    distance: "90 km",
    time: "2 hours",
    cars: ["Sedan - ₹3,020", "Innova - ₹3,920", "Innova Crysta - ₹4,640"],
    highlights: ["Chettinad Mansions", "Chettinad Cuisine", "Cultural Heritage"],
    benefits: ["Heritage tour experts", "Foodie-friendly stops", "Comfortable rides", "Local guides available", "Fixed pricing", "Safe for families"],
    content: `Explore the architectural wonders of Chettinad with our Trichy to Karaikudi taxi service. Karaikudi is the heart of Chettinad, famous for its magnificent mansions and world-renowned cuisine.`,
  },
  "trichy-to-kumbakonam": {
    city: "Kumbakonam",
    distance: "105 km",
    time: "2.5 hours",
    cars: ["Sedan - ₹3,440", "Innova - ₹4,490", "Innova Crysta - ₹5,330"],
    highlights: ["Temple City of India", "Mahamaham Tank", "Traditional Crafts"],
    benefits: ["Pilgrimage ready", "Multiple temple stops", "Knowledgeable drivers", "Safe and secure", "Transparent billing", "Comfortable rides"],
    content: `Kumbakonam is famous for its numerous temples and traditional South Indian culture. Our taxi service from Trichy to Kumbakonam is perfect for families on a pilgrimage tour.`,
  },
  "trichy-to-sivagangai": {
    city: "Sivagangai",
    distance: "135 km",
    time: "2.5 hours",
    cars: ["Sedan - ₹4,280", "Innova - ₹5,630", "Innova Crysta - ₹6,710"],
    highlights: ["Sivagangai Palace", "Vettangudi Bird Sanctuary", "Historical Heritage"],
    benefits: ["Safe family travel", "Punctual service", "Experienced drivers", "Clean vehicles", "Knowledgeable about routes", "Fixed rates"],
    content: `Sivagangai is a place of historical importance in Tamil Nadu. Our taxi service provides a comfortable and safe journey from Trichy to Sivagangai, perfect for visiting the palace or the bird sanctuary.`,
  },
  "trichy-to-orthanadu": {
    city: "Orthanadu",
    distance: "80 km",
    time: "2 hours",
    cars: ["Sedan - ₹2,740", "Innova - ₹3,540", "Innova Crysta - ₹4,180"],
    highlights: ["Agricultural Hub", "Serene Countryside", "Quiet surroundings"],
    benefits: ["Direct point-to-point", "No hidden charges", "Friendly drivers", "Clean cars", "Reliable timing", "24/7 booking"],
    content: `Orthanadu is a beautiful town surrounded by lush greenery. Our Trichy to Orthanadu cab service is highly reliable and affordable for all travelers.`,
  },
  "trichy-to-ramanathapuram": {
    city: "Ramanathapuram",
    distance: "180 km",
    time: "3.5 hours",
    cars: ["Sedan - ₹5,540", "Innova - ₹7,340", "Innova Crysta - ₹8,780"],
    highlights: ["Ramalingavilasam Palace", "Marine Biosphere Reserve", "Gateway to Rameswaram"],
    benefits: ["Long distance expertise", "Highway safety", "Comfortable multi-hour ride", "Luggage assistance", "Professional chauffeurs", "GPS enabled safe rides"],
    content: `Ramanathapuram, also known as Ramnad, is rich in history and maritime heritage. Our service ensures a smooth journey along the coastal highways.`,
  },
  "trichy-to-karur": {
    city: "Karur",
    distance: "90 km",
    time: "2 hours",
    cars: ["Sedan - ₹3,020", "Innova - ₹3,920", "Innova Crysta - ₹4,640"],
    highlights: ["Pasupatheeswarar Temple", "Textile Hub", "River Amaravathi"],
    benefits: ["Business trip specialized", "Punctual service", "Clean interiors", "Smooth highway driving", "Professional conduct", "Reliable transport"],
    content: `Karur is one of the oldest cities in Tamil Nadu and a major textile exporter. Our Trichy to Karur taxi service is preferred by both business travelers and spiritual visitors.`,
  },
  "trichy-to-ariyalur": {
    city: "Ariyalur",
    distance: "85 km",
    time: "2 hours",
    cars: ["Sedan - ₹2,880", "Innova - ₹3,730", "Innova Crysta - ₹4,410"],
    highlights: ["Gangaikonda Cholapuram", "Fossil Museum", "Industrial Town"],
    benefits: ["Affordable pricing", "Experienced local drivers", "Direct route connectivity", "Safe and secure", "24/7 support", "Clean vehicles"],
    content: `Ariyalur is famous for celestial architecture and industrial growth. Visit the UNESCO site Gangaikonda Cholapuram with our comfortable taxi service.`,
  },
  "trichy-to-aranthangi": {
    city: "Aranthangi",
    distance: "80 km",
    time: "2 hours",
    cars: ["Sedan - ₹2,740", "Innova - ₹3,540", "Innova Crysta - ₹4,180"],
    highlights: ["Aranthangi Fort", "Vayalagam", "Rural Beauty"],
    benefits: ["Quick booking", "Professional drivers", "Clean cars", "Transparent billing", "Safety first approach", "Flexible schedules"],
    content: `Aranthangi is a significant town in the Pudukkottai district. Our service provides a reliable link between Trichy and Aranthangi for all your travel needs.`,
  },
  "trichy-to-pattukottai": {
    city: "Pattukottai",
    distance: "100 km",
    time: "2.5 hours",
    cars: ["Sedan - ₹3,300", "Innova - ₹4,300", "Innova Crysta - ₹5,100"],
    highlights: ["Coconut Capital", "Serene landscapes", "Quiet town life"],
    benefits: ["Comfortable long drives", "Safety ensured", "Professional drivers", "Well-maintained fleet", "Fixed rates", "Instant confirmation"],
    content: `Pattukottai is known as the coconut hub of the Delta region. Travel comfortably from Trichy to Pattukottai with our specialized outstation taxi service.`,
  },
  "trichy-to-mannargudi": {
    city: "Mannargudi",
    distance: "100 km",
    time: "2.5 hours",
    cars: ["Sedan - ₹3,300", "Innova - ₹4,300", "Innova Crysta - ₹5,100"],
    highlights: ["Rajagopalaswamy Temple", "Haridranadhi tank", "Temple Town"],
    benefits: ["Pilgrimage expertise", "Ritual timings awareness", "Comfortable for families", "Clean cars", "Ethical pricing", "Reliable support"],
    content: `Mannargudi is famous for the majestic Rajagopalaswamy Temple. Our drivers are well-versed with the religious importance of this town and ensure a timely arrival for darshan.`,
  },
  "trichy-to-paramakudi": {
    city: "Paramakudi",
    distance: "160 km",
    time: "3.5 hours",
    cars: ["Sedan - ₹4,980", "Innova - ₹6,580", "Innova Crysta - ₹7,860"],
    highlights: ["Vaigai River", "Historical significance", "Bustling town"],
    benefits: ["Experienced long-distance drivers", "Comfortable vehicles", "Safe highway navigation", "GPS tracking", "Fixed fares", "24/7 service"],
    content: `Paramakudi is a major town in the Ramanathapuram district. Our taxi service ensures a fast and safe journey from Trichy to Paramakudi.`,
  },
  "trichy-to-thirumayam": {
    city: "Thirumayam",
    distance: "60 km",
    time: "1.5 hours",
    cars: ["Sedan - ₹2,180", "Innova - ₹2,780", "Innova Crysta - ₹3,260"],
    highlights: ["Thirumayam Fort", "Sathyagiriswarar Temple", "Archaeological site"],
    benefits: ["Historical tour expertise", "Short trip special rates", "Punctual service", "Clean and safe", "Knowledgeable drivers", "Quick booking"],
    content: `Thirumayam is famous for its massive rock-cut fort and ancient cave temples. Our service is perfect for a quick historical day trip from Trichy.`,
  },
};

const allRoutes = [
  { slug: "trichy-to-velankanni", label: "Trichy to Velankanni" },
  { slug: "trichy-to-rameswaram", label: "Trichy to Rameswaram" },
  { slug: "trichy-to-tanjore", label: "Trichy to Tanjore" },
  { slug: "trichy-to-pudukkottai", label: "Trichy to Pudukkottai" },
  { slug: "trichy-to-madurai", label: "Trichy to Madurai" },
  { slug: "trichy-to-dindigul", label: "Trichy to Dindigul" },
  { slug: "trichy-to-namakkal", label: "Trichy to Namakkal" },
  { slug: "trichy-to-karaikudi", label: "Trichy to Karaikudi" },
  { slug: "trichy-to-kumbakonam", label: "Trichy to Kumbakonam" },
  { slug: "trichy-to-thiruvarur", label: "Trichy to Thiruvarur" },
  { slug: "trichy-to-sivagangai", label: "Trichy to Sivagangai" },
  { slug: "trichy-to-orthanadu", label: "Trichy to Orthanadu" },
  { slug: "trichy-to-ramanathapuram", label: "Trichy to Ramanathapuram" },
  { slug: "trichy-to-karur", label: "Trichy to Karur" },
  { slug: "trichy-to-ariyalur", label: "Trichy to Ariyalur" },
  { slug: "trichy-to-aranthangi", label: "Trichy to Aranthangi" },
  { slug: "trichy-to-pattukottai", label: "Trichy to Pattukottai" },
  { slug: "trichy-to-mannargudi", label: "Trichy to Mannargudi" },
  { slug: "trichy-to-paramakudi", label: "Trichy to Paramakudi" },
  { slug: "trichy-to-thirumayam", label: "Trichy to Thirumayam" },
];

const RoutePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const route = routeData[slug || ""];

  if (!route) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Route Not Found</h1>
            <Link to="/" className="btn-primary">Go Home</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title={`Trichy to ${route.city} Taxi Fare | Book Cab at Best Price`}
        description={`Book Trichy to ${route.city} taxi at best price. Distance: ${route.distance}, travel time: ${route.time}. One-way & round trip cabs available with professional drivers. Call +91 74187 49974.`}
        keywords={`trichy to ${route.city} taxi, trichy to ${route.city} cab fare, ${route.city} taxi from trichy, trichy to ${route.city} taxi price, one way cab trichy to ${route.city}, outstation taxi trichy to ${route.city}, cheap taxi trichy to ${route.city}`}
        canonical={`https://trichytovelankanni.taxi/routes/${slug}`}
      />

      {/* Hero */}
      <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <motion.div {...fadeUp} className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-primary">Trichy to {route.city}</span>
          </motion.div>
          <motion.h1 {...fadeUp} className="text-3xl sm:text-5xl font-bold">
            Trichy to <span className="text-gradient">{route.city}</span> Taxi
          </motion.h1>
          <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl">
            Book your comfortable and affordable cab from Trichy to {route.city}. {route.distance} distance, approximately {route.time} travel time.
          </motion.p>
        </div>
      </section>

      {/* Route Info Cards */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
            <motion.div {...fadeUp} className="glass-card rounded-2xl p-6 text-center">
              <MapPin className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Distance</p>
              <p className="text-2xl font-bold text-foreground">{route.distance}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="glass-card rounded-2xl p-6 text-center">
              <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Travel Time</p>
              <p className="text-2xl font-bold text-foreground">{route.time}</p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-10">
              {/* Pricing Table */}
              <motion.div {...fadeUp}>
                <h2 className="text-2xl font-bold text-foreground mb-5">Taxi Fare: Trichy to {route.city}</h2>
                <div className="glass-card rounded-2xl overflow-x-auto no-scrollbar">
                  <table className="w-full min-w-[300px]">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="p-4 text-xs sm:text-sm font-semibold text-foreground whitespace-nowrap">Available Car Types</th>
                      </tr>
                    </thead>
                    <tbody>
                      {route.cars.map((p) => (
                        <tr key={p} className="border-b border-border/50">
                          <td className="p-4 flex items-center gap-2 text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                            <Car className="w-4 h-4 text-primary flex-shrink-0" /> {p}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* Route Highlights */}
              <motion.div {...fadeUp}>
                <h2 className="text-2xl font-bold text-foreground mb-5">Route Highlights</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {route.highlights.map((h) => (
                    <span key={h} className="flex items-center gap-2 text-sm text-muted-foreground glass-card rounded-lg p-3">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" /> {h}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div {...fadeUp}>
                <h2 className="text-2xl font-bold text-foreground mb-5">Why Choose Our Taxi?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {route.benefits.map((b) => (
                    <span key={b} className="flex items-center gap-2 text-sm text-muted-foreground glass-card rounded-lg p-3">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" /> {b}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* SEO Content */}
              <motion.div {...fadeUp}>
                <article className="prose prose-invert prose-sm max-w-none text-muted-foreground">
                  {route.content.split("\n\n").map((p, i) => (
                    <p key={i} className="mb-4 leading-relaxed">{p}</p>
                  ))}
                </article>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <BookingForm idPrefix="route" />
              <motion.div {...fadeUp} className="glass-card rounded-2xl p-6 text-center">
                <p className="text-foreground font-semibold mb-2">Need Help Booking?</p>
                <p className="text-sm text-muted-foreground mb-4">Call us for instant booking</p>
                <a href="tel:+917418749974" className="btn-primary w-full flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> +91 74187 49974
                </a>
              </motion.div>

              {/* Other Routes */}
              <div className="glass-card rounded-2xl p-6">
                <h3 className="text-foreground font-semibold mb-4">Other Popular Routes</h3>
                <div className="space-y-2">
                  {allRoutes.filter((r) => r.slug !== slug).map((r) => (
                    <Link key={r.slug} to={`/routes/${r.slug}`} className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors py-2">
                      {r.label} <ChevronRight className="w-4 h-4" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RoutePage;
